import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Dialog, DialogContent } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useAddOrderItemsFromPartsMutation } from '@/app/services/orderItems'
import { useCreateOrderMutation } from '@/app/services/orders'
import { selectLocale } from '@/app/slices/appSlice'
import { selectContactId } from '@/app/slices/contactsSlice'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn } from '@/app/slices/guestUserSlice'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { ORDER_USER_TYPE } from '@/common/utils'

import QuickPart from '../../../quick-part/QuickPart'

const classes = {
    dialogContent: {
        display: 'flex',
        flexDirection: 'row',
        p: 0,
    },
}

const AddQuickPartDialog = ({ onClose, open }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const { isAuthenticated } = useKindeAuth()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const organisation = useSelector(selectOrganisation)
    const currentCustomerId = useSelector(selectCustomerId)
    const currentContactId = useSelector(selectContactId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)
    const locale = useSelector(selectLocale)

    const [createOrder, { isLoading: isCreating, reset: resetCreateOrder }] = useCreateOrderMutation()
    const [uploadOrderItems, { isLoading: isUploading, reset: resetUploadOrderItems }] =
        useAddOrderItemsFromPartsMutation()

    const handleClose = () => {
        onClose()
    }

    const handleQuickPartSubmit = async (drawing) => {
        let newOrderId = null
        const userType = isAuthenticated ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest
        const contactId = isAuthenticated ? currentContactId : isGuestLoggedIn ? guestUser.contactId : null
        const customerId = isAuthenticated ? currentCustomerId : isGuestLoggedIn ? guestUser.customerId : null

        const partsToAdd = {
            drawing: drawing,
            partSource: 'quickpart',
        }

        if (!orderId) {
            try {
                const createOrderResponse = await createOrder({
                    organisationId,
                    payload: {
                        organisationId,
                        quoteUserType: userType,
                        customerContactId: contactId,
                        customerId: customerId,
                    },
                }).unwrap()
                newOrderId = createOrderResponse.id
            } catch (e) {
                enqueueSnackbar(t('Failed to create order'), {
                    variant: 'error',
                })

                handleClose()
                resetUploadOrderItems()
                resetCreateOrder()
                return
            }
        }

        try {
            await uploadOrderItems({
                organisationId,
                orderId: newOrderId || orderId,
                parts: [partsToAdd],
            }).unwrap()
            enqueueSnackbar(`${t('Quick Part added to order')}`, { variant: 'success' })
        } catch (error) {
            const errorMessage = t('$t(An error occurred) adding Quick Part')
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        } finally {
            handleClose()
            resetUploadOrderItems()
            resetCreateOrder()
        }

        if (newOrderId) {
            navigate(`orders/${newOrderId}`)
        }
    }

    return (
        <Dialog
            fullScreen={true}
            open={open}
            sx={classes.dialogRoot}
            onClose={handleClose}
        >
            <DialogContent sx={classes.dialogContent}>
                <QuickPart
                    disabledButtons={isCreating || isUploading}
                    drawingUnits={organisation.defaultDrawingUnits}
                    locale={locale}
                    nqp={true}
                    onCancelClicked={handleClose}
                    onSubmitClicked={handleQuickPartSubmit}
                />
            </DialogContent>
        </Dialog>
    )
}

AddQuickPartDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default AddQuickPartDialog
